import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import { uuidv7 } from 'uuidv7'

import {
  TENDERS,
  allocateTenders,
  roundCashTender,
  type CashRounding,
  type Tender,
  type TenderLine,
} from '@lensmira/domain'

import { formatMoneyForDisplay } from '../money.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { Ltr, useTranslation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { tenderLabel } from '../status-labels.js'

/**
 * Taking a payment against a confirmed order. §6.7 `POS-002`, `POS-003`; Appendix D `MR-08`;
 * `BR-08`. Posts to `POST /orders/payments`, `apps/api/src/orders/payments.service.ts`.
 *
 * `POS-002`: "An optical order is routinely 50% at order, balance at collection." So this screen
 * never demands full settlement before it will save. A tender total short of what is owed is the
 * ordinary case, not an error, and the server agrees: it leaves the order `partially_paid` rather
 * than refusing it.
 *
 * **`amountDueMinor` is a prop, not a read this screen performs.** There is no GET in the task this
 * screen was built from, and `OFF-001b`'s cached working set for reads is a later slice. Whoever
 * shows a customer their order already knows what is owed; asking this screen to fetch it again
 * would be a second source of truth for a figure the server is the only correct owner of.
 *
 * **Nothing here computes the allocation independently either.** `allocateTenders` and
 * `roundCashTender` are the exact same pure functions `payments.service.ts` calls, from
 * `packages/domain`. The running "outstanding" and "change" shown while the cashier is still
 * typing is not a second implementation of `MR-08` that could quietly drift from the one the server
 * enforces, it is that implementation, run here first.
 *
 * **A cash tender needs a drawer, and this does not wait for the server to say so.** `BR-08`
 * requires a cash session on every cash tender, and `resolveCashSession` refuses one with none as
 * `CASH_SESSION_REQUIRED`. Waiting for that round trip is a real cost here: a refused write sits
 * `blocked` in the queue, possibly a long time on a bad connection, before anyone learns the drawer
 * was never opened. So the same condition is checked before the button is even enabled, in the same
 * words the server uses, and the request is never sent.
 *
 * `POS-010a`'s cash rounding is a tenant setting, not something typed at the till, so it arrives as
 * an optional prop and is only ever passed through. Nothing here invents a denomination:
 * `CLAUDE.md` is explicit that a guessed threshold moves real money, and Appendix D's `PK-030`
 * names no value.
 *
 * `POS-008a`, also named against this screen, is the per-role discount limit: it governs a price
 * override at order entry and has no bearing on collecting a payment against a total that is
 * already fixed. Not implemented here; see `integrationNotes` in this build's report.
 */

export interface CashRoundingConfig {
  /** Minor units, as a decimal string. `PK-030` names no default. */
  readonly smallestDenominationMinor: string
  readonly halfGoes: 'up' | 'down'
}

export interface TakePaymentProps {
  readonly orderId: string
  /** `BR-05`: the branch taking the money, which need not be the branch that made the sale. */
  readonly branchId: string
  /** `BR-08`: every sale records its terminal. */
  readonly terminalId: string
  /** `BR-08`. Absent means no open cash session on this terminal. Required only if any tender is cash. */
  readonly cashSessionId?: string
  /** What the order still owes, right now, in minor units as a decimal string. */
  readonly amountDueMinor: string
  /** ISO 4217, e.g. "PKR". */
  readonly currency: string
  /** The tenant's configured cash rounding, if any. Never defaulted here. */
  readonly cashRounding?: CashRoundingConfig
  readonly onSaved?: () => void
}

interface Row {
  readonly id: string
  readonly method: Tender
  /** What the cashier typed, verbatim. Parsed only for the running totals, never held as a number. */
  readonly amountText: string
}

/**
 * A decimal amount the cashier typed ("1500", "1,500.5", "1500.50") to minor units.
 *
 * No `Number()` or `parseFloat()` anywhere in the path. `MR-01`: "All money is stored as integer
 * minor units... Never a float." A typed amount is exactly as much money as a stored one, and a
 * float roundtrip is where precision goes missing.
 *
 * Assumes a two-decimal currency, which every currency named in the spec is (Appendix D and §13
 * both write amounts in rupees and paisa). A zero-decimal currency is out of scope for v1.
 */
function parseMinor(text: string): bigint | null {
  const trimmed = text.trim().replace(/,/g, '')
  if (trimmed === '') return null
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed)
  if (!match) return null
  const whole = match[1] ?? '0'
  const fraction = (match[2] ?? '').padEnd(2, '0')
  return BigInt(whole) * 100n + BigInt(fraction)
}

/**
 * Minor units to something a cashier reads.
 *
 * Was eight lines of its own, and both details it invented were wrong. `/ 100n` and `% 100n`
 * hardcoded two decimal places, so `decimalsFor` never ran and a JPY or KWD amount rendered at the
 * wrong scale, which `MR-01` makes a different amount of money rather than a formatting slip. And
 * the sign was a U+2212 MINUS SIGN placed before the currency code, where every other screen and
 * every assertion in this repository writes an ASCII hyphen after it.
 */
function formatMinor(minorValue: bigint, currency: string): string {
  return formatMoneyForDisplay(minorValue, currency)
}

export function TakePayment({
  orderId,
  branchId,
  terminalId,
  cashSessionId,
  amountDueMinor,
  currency,
  cashRounding,
  onSaved,
}: TakePaymentProps): JSX.Element {
  const [rows, setRows] = useState<Row[]>(() => [{ id: uuidv7(), method: 'cash', amountText: '' }])
  const [writeId, setWriteId] = useState<string | null>(null)
  /**
   * `ARC-006`: "A double-tapped 'take payment' must not charge twice." The idempotency key the
   * queue mints protects a retry of *one* queued write, never a second `enqueue` call, and
   * `POS-002`'s ordinary partial payment does not stop a second one from being accepted server
   * side: `partially_paid` with money still owed passes every one of `payments.service.ts`'s
   * gates the same as the first tap did. So the tap itself is what has to be made singular, the
   * same way `SignIn.tsx` makes a submit singular.
   */
  const [busy, setBusy] = useState(false)
  const { enqueue, snapshot } = useWriteQueue()
  const { t } = useTranslation()
  const firstAmount = useRef<HTMLInputElement>(null)

  /**
   * The parent is told when the **server** has the payment, not when the queue has it.
   *
   * `onSaved` re-reads the order so the outstanding figure on screen is the server's. Called on the
   * line after `enqueue` resolved, that read ran before the POST was sent and answered with the
   * balance from before the payment: PKR 5,000 taken, the till still showing the whole 10,880.99
   * owing and no payment listed, which is an invitation to collect it twice.
   */
  useWriteSettled(snapshot, writeId, () => {
    onSaved?.()
  })

  useEffect(() => {
    // KB-02: the first field a cashier needs is focused on open, no click to start.
    firstAmount.current?.focus()
  }, [])

  const due = BigInt(amountDueMinor)

  const tenderLines: TenderLine[] = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        method: row.method,
        amountMinor: parseMinor(row.amountText) ?? 0n,
      })),
    [rows],
  )

  // POS-010a, MR-06: rounding applies at tender and only when every tender is cash, the same
  // condition payments.service.ts checks before calling roundCashTender.
  const allCash = tenderLines.length > 0 && tenderLines.every((line) => line.method === 'cash')
  const rounding: CashRounding | undefined = cashRounding
    ? {
        smallestDenominationMinor: BigInt(cashRounding.smallestDenominationMinor),
        halfGoes: cashRounding.halfGoes,
      }
    : undefined
  const rounded =
    rounding && allCash
      ? roundCashTender(due, rounding)
      : { payableMinor: due, roundingDifferenceMinor: 0n }

  // MR-08: split tender allocates to the total, never per line. Same function the server runs.
  const allocation = allocateTenders(rounded.payableMinor, tenderLines)

  const hasCash = tenderLines.some((line) => line.method === 'cash' && line.amountMinor > 0n)
  const missingCashSession = hasCash && (cashSessionId === undefined || cashSessionId === '')
  const anyTyped = rows.some((r) => r.amountText.trim() !== '')
  const anyUnparseable = rows.some(
    (r) => r.amountText.trim() !== '' && parseMinor(r.amountText) === null,
  )
  // "Every line is positive" rather than "no line is non-positive". The same test either way,
  // including on an empty list, and it says what the button needs rather than what it refuses.
  const allPositive = tenderLines.every((line) => line.amountMinor > 0n)
  // writeId !== null: once this screen has queued a payment, it does not queue a second one.
  // Taking another payment against the same order is a new screen, not another tap of this button.
  const canSave =
    anyTyped && !anyUnparseable && allPositive && !missingCashSession && writeId === null

  // One whole sentence per case; the rounded prefix is a different message rather than a second
  // `t()` call glued on, because the order of the two halves is the language's to decide.
  const roundedTo = <Ltr>{formatMinor(rounded.payableMinor, currency)}</Ltr>
  const isRounded = rounded.roundingDifferenceMinor !== 0n
  /**
   * Each figure goes in as an element rather than as text, and the paragraph below no longer
   * carries `dir="ltr"`.
   *
   * The blunt version worked and could not survive translation: forcing the whole paragraph
   * left-to-right protects the amount by rendering an Urdu sentence backwards, which is a worse
   * defect than the one it prevents and only shows up the day `ur.ts` gains these keys.
   * `sentence()` keeps it one `t()` call (Appendix S) with each amount isolated on its own.
   */
  const tenderSummary =
    allocation.outstandingMinor > 0n
      ? isRounded
        ? sentence(t, 'takePayment.roundedStillOutstanding', {
            rounded: roundedTo,
            amount: <Ltr>{formatMinor(allocation.outstandingMinor, currency)}</Ltr>,
          })
        : sentence(t, 'takePayment.stillOutstanding', {
            amount: <Ltr>{formatMinor(allocation.outstandingMinor, currency)}</Ltr>,
          })
      : allocation.changeMinor > 0n
        ? isRounded
          ? sentence(t, 'takePayment.roundedChangeDue', {
              rounded: roundedTo,
              amount: <Ltr>{formatMinor(allocation.changeMinor, currency)}</Ltr>,
            })
          : sentence(t, 'takePayment.changeDue', {
              amount: <Ltr>{formatMinor(allocation.changeMinor, currency)}</Ltr>,
            })
        : isRounded
          ? sentence(t, 'takePayment.roundedFullyTendered', { rounded: roundedTo })
          : t('takePayment.fullyTendered')

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { id: uuidv7(), method: 'cash', amountText: '' }])
  }, [])

  const removeRow = useCallback((id: string) => {
    // Never below one tender: a payment with none is not a payment.
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)))
  }, [])

  const updateMethod = useCallback((id: string, method: Tender) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, method } : r)))
  }, [])

  const updateAmount = useCallback((id: string, amountText: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, amountText } : r)))
  }, [])

  const save = useCallback(async () => {
    // The guard, not just the disabled attribute: `busy` is set below before the first `await`,
    // so it is already true, synchronously, for a second call arriving before this render commits.
    if (busy || writeId !== null) return
    setBusy(true)
    // OFF-001a: an enqueue, not a fetch. ARC-006's idempotency key protects a retry of this one
    // write; it is the `busy`/`writeId` guard above that keeps this call itself to one per screen.
    const write = (await enqueue({
      method: 'POST',
      path: '/orders/payments',
      label: t('takePayment.queueLabel', { orderId }),
      body: {
        orderId,
        branchId,
        terminalId,
        ...(cashSessionId === undefined ? {} : { cashSessionId }),
        tenders: tenderLines.map((line) => ({
          method: line.method,
          amountMinor: line.amountMinor.toString(),
        })),
        ...(cashRounding === undefined ? {} : { cashRounding }),
      },
    })) as { id: string }
    setBusy(false)
    setWriteId(write.id)
  }, [
    branchId,
    busy,
    cashRounding,
    cashSessionId,
    enqueue,
    orderId,
    tenderLines,
    t,
    terminalId,
    writeId,
  ])

  return (
    <section className="lm-screen" aria-labelledby="take-payment-heading">
      <h1 id="take-payment-heading" className="lm-screen__heading">
        {t('app.takePayment')}
      </h1>

      <p className="lm-hint">
        {sentence(t, 'takePayment.amountDue', {
          amount: <Ltr>{formatMinor(due, currency)}</Ltr>,
        })}
      </p>

      <div className="lm-form">
        {rows.map((row, index) => (
          <div key={row.id} className="lm-screen__actions">
            <label className="lm-field">
              <span className="lm-field__label">
                {t('takePayment.tenderMethod', { number: index + 1 })}
              </span>
              <select
                className="lm-input"
                value={row.method}
                onChange={(e) => updateMethod(row.id, e.currentTarget.value as Tender)}
              >
                {TENDERS.map((tender) => (
                  <option key={tender} value={tender}>
                    {tenderLabel(t, tender)}
                  </option>
                ))}
              </select>
            </label>

            <label className="lm-field">
              <span className="lm-field__label">
                {t('takePayment.tenderAmount', { number: index + 1, currency })}
              </span>
              <input
                ref={index === 0 ? firstAmount : undefined}
                className="lm-input"
                inputMode="decimal"
                autoComplete="off"
                value={row.amountText}
                onChange={(e) => updateAmount(row.id, e.currentTarget.value)}
              />
            </label>

            {rows.length > 1 ? (
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => removeRow(row.id)}
              >
                {t('takePayment.removeTender', { number: index + 1 })}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <div className="lm-screen__actions">
        <button type="button" className="lm-button lm-button--secondary" onClick={addRow}>
          {t('takePayment.addTender')}
        </button>
      </div>

      {/* POS-003, MR-08: what is left, or the change due, read back as the cashier types. */}
      <p className="lm-hint" role="status">
        {tenderSummary}
      </p>

      {missingCashSession ? (
        <p className="lm-hint" role="status">
          {t('takePayment.cashSessionRequired')}
        </p>
      ) : null}
      {anyUnparseable ? (
        <p className="lm-hint" role="status">
          {t('takePayment.amountUnparseable', { currency })}
        </p>
      ) : null}

      <div className="lm-screen__actions">
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={!canSave || busy}
          onClick={() => {
            void save()
          }}
        >
          {busy ? t('takePayment.submitBusy') : t('app.takePayment')}
        </button>
        <p className="lm-hint">{t('takePayment.partialAllowed')}</p>
      </div>

      {/* OFF-005: staff must never be uncertain whether the payment is safe. */}
      <p className="lm-queue-state" role="status">
        {statusLine(t, snapshot, writeId, t('queue.subject.payment'))}
      </p>
    </section>
  )
}
